import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import moment from 'moment-timezone'
import {
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import API from '../../utils/API'
import { getTimezoneOffsetMinutes } from '../../utils/timezone'
import { useSessionTimezone } from '../../hooks/useSessionTimezone'
import {
  convertDateAndSlotToUTC,
  getInitialDate,
  getInitialSlot,
} from '../../utils/sessionDateUtils'
import { generateRecurringDates } from '../../utils/recurringDateUtils'
import {
  mergeDatesWithAvailability,
  getUnavailableSlotsTooltip,
  hasBlockingUnavailableSlots,
  getSchedulableDatesFromList,
  toggleDateIncludedInList,
  isWeeklyWarning,
  isHardSchedulingBlock,
  isSessionRestrictionSlot,
  evaluateSlotAvailabilityItem,
  SlotAvailabilityCheckResult,
  FIRST_SESSION_REQUIRED_MESSAGE,
  isFirstSessionScheduling,
  isFirstSessionMissing,
} from '@abroad/rest-client'
import useSchedulingRestrictions from '../../hooks/useSchedulingRestrictions'
import WeeklySessionWarningModal from './WeeklySessionWarningModal'
import {
  handleRescheduleSession,
  handleScheduleFirstSession,
  handleScheduleRemainingSessions,
  handleScheduleAllSessions,
} from '../../utils/schedulingHandlers'
import SessionDatePicker from './SessionDatePicker'
import TimeSlotPicker from './TimeSlotPicker'
import ScheduleSettingsModal from './ScheduleSettingsModal'
import SelectedDatesList from './SelectedDatesList'
import CoachDetailsSection from './CoachDetailsSection'
import ScheduleButton from './ScheduleButton'
import {
  gtmRescheduleSessionAction,
  gtmScheduleAllSessionAction,
} from '../quests/gtmfunctions'

/**
 * Reusable Session Scheduling Calendar Component
 * Handles multiple scheduling flows:
 * - First session scheduling
 * - Remaining sessions scheduling
 * - Rescheduling existing session
 * - Schedule Multiple (future - multiple selection)
 */
const SessionSchedulingCalendar = ({
  schedulingType = 'first-session', // 'first-session' | 'remaining' | 'reschedule' | 'schedule-all'
  coachDetails = null,
  scheduledSession = null, // For reschedule flow - contains existing session data
  allowMultipleSelection = false, // For schedule-all flow (future)
  onScheduleSuccess = null, // Callback after successful scheduling
}) => {
  const history = useHistory()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const { user } = useUserService()

  const getFname = (person) => {
    const name = person?.fullName
    return name && String(name).trim() ? String(name).trim() : '-'
  }

  const getSessionLabelForTracking = (session) => {
    const sessionIndex =
      session?.schedulingCondition?.sessionIndex || session?.sessionIndex
    const sowType = session?.sowType || session?.sow?.sowType
    const coach = getFname(session?.coach || session?.coachId)
    const client = getFname(session?.client)

    let sessionLabel = 'Session'
    if (sowType === 'QUEST') {
      if (sessionIndex === 1) sessionLabel = 'Preparation Session'
      else if (sessionIndex === 2) sessionLabel = 'Integration Session'
      else if (sessionIndex === 3) sessionLabel = 'Experience Session'
      else if (sessionIndex) sessionLabel = `Session ${sessionIndex}`
    } else if (sessionIndex) {
      sessionLabel = `Session ${sessionIndex}`
    } else if (session?.isGrowthEvent) {
      sessionLabel = 'Growth Session'
    } else {
      sessionLabel = 'Coaching Session'
    }

    if (coach !== '-' || client !== '-') {
      return `${sessionLabel}: ${coach} & ${client}`
    }
    return sessionLabel
  }

  // Extract coach information (API provides coach object with fname)
  const coachName =
    getFname(coachDetails?.coach) !== '-'
      ? getFname(coachDetails?.coach)
      : getFname(scheduledSession?.coach)
  const coachId = coachDetails?.coachId || scheduledSession?.coachId

  // Duration handling:
  // - For first session flows, use firstSlotDuration from scheduleConfig when hasGrowthInRemaining is true
  // - For remaining / schedule-all / reschedule, use otherSlotsDuration from scheduleConfig
  // - Fallback to old structure (firstSessionDuration/allSessionDuration) for backward compatibility
  const scheduleConfig = coachDetails?.scheduleConfig
  const hasGrowthInRemaining = scheduleConfig?.hasGrowthInRemaining
  const firstSlotDuration = scheduleConfig?.firstSlotDuration
  const otherSlotsDuration = scheduleConfig?.otherSlotsDuration
  const sowType =
    coachDetails?.sowType ||
    coachDetails?.sow?.sowType ||
    scheduledSession?.sowType

  // Legacy support for old structure
  const firstSessionDuration = firstSlotDuration || scheduledSession?.duration
  const allSessionsDuration = otherSlotsDuration || scheduledSession?.duration

  // Determine duration based on scheduling type and scheduleConfig
  const sowSessionDuration =
    schedulingType === 'first-session' && hasGrowthInRemaining
      ? firstSlotDuration || firstSessionDuration
      : otherSlotsDuration || allSessionsDuration
  const sowId =
    coachDetails?.sowId || coachDetails?.sow?._id || scheduledSession?.sowId
  const eventId = scheduledSession?.eventId || scheduledSession?._id

  const handleSchedulingSuccess = useCallback(
    (data) => {
      if (schedulingType === 'first-session') {
        Notification.showNotification(
          'Your first session has been scheduled!',
          'success',
        )
        if (onScheduleSuccess) {
          onScheduleSuccess(data?.event)
        } else {
          history.push('/coaching/sessions', {
            scheduledEvent: data?.event,
          })
        }
        return
      }

      if (schedulingType === 'schedule-all') {
        Notification.showNotification(
          data?.message || 'Your sessions have been scheduled successfully!',
          'success',
        )
        if (onScheduleSuccess) {
          onScheduleSuccess(data?.scheduledSessions)
        } else {
          history.push('/coaching/sessions', {
            scheduledSessions: data?.scheduledSessions,
          })
        }
      }
    },
    [schedulingType, onScheduleSuccess, history, Notification],
  )

  const {
    weeklyModal,
    isConfirmingWeekly,
    closeWeeklyModal,
    handleWeeklyConfirm,
    runScheduling,
  } = useSchedulingRestrictions({
    onSuccess: handleSchedulingSuccess,
    onSameDayConflict: (message) => {
      Notification.showNotification(message, 'danger')
    },
    onError: (message) => {
      Notification.showNotification(message, 'danger')
    },
  })

  // Use timezone hook
  const { clientTimezone, handleTimezoneChange, defaultTimezone } =
    useSessionTimezone()

  // For reschedule flow, pre-fill with existing session date/time
  const initialDate = getInitialDate(
    schedulingType,
    scheduledSession,
    defaultTimezone,
  )
  const initialSlot = getInitialSlot(schedulingType, scheduledSession)

  const [selectedDate, setSelectedDate] = useState(initialDate)
  const [selectedSlot, setSelectedSlot] = useState(initialSlot)
  const [timeSlots, setTimeSlots] = useState([])
  const [availableDays, setAvailableDays] = useState({})
  const [currentMonth, setCurrentMonth] = useState(
    moment(initialDate).startOf('month'),
  )
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  const preferredDateRef = useRef(selectedDate)
  const [timeFormat, setTimeFormat] = useState('12h')
  const [isScheduling, setIsScheduling] = useState(false)

  // Schedule-all flow states
  const [showScheduleSettingsModal, setShowScheduleSettingsModal] =
    useState(false)
  const [selectedDatesList, setSelectedDatesList] = useState([])
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [rescheduleSlotWeeklyModal, setRescheduleSlotWeeklyModal] = useState({
    isOpen: false,
    message: '',
  })
  const pendingRescheduleSubmitRef = useRef(false)
  const [editingDateIndex, setEditingDateIndex] = useState(null)
  const [editingBaseDate, setEditingBaseDate] = useState(null) // original date for the slot being edited
  const [showSelectedDates, setShowSelectedDates] = useState(false)
  const skipNextLoadRef = useRef(false)
  const editingDateIndexRef = useRef(null)
  const [editingduration, setEditingduration] = useState(null) // duration from unavailable slot

  // For schedule-all flow: identify which slots on the currently selected date
  // are already taken by other selections (so we can show them as disabled in
  // the time slots list). We intentionally exclude the slot being edited
  // (editingDateIndex) so that the user can keep the same time on the same date
  // if needed.
  const takenSlotsForCurrentDate = useMemo(() => {
    if (schedulingType !== 'schedule-all' || !selectedDate) {
      return []
    }

    return selectedDatesList
      .filter(
        (item, index) =>
          item?.date === selectedDate &&
          item?.slotStart &&
          item?.slotEnd &&
          index !== editingDateIndex,
      )
      .map((item) => item.slotStart)
  }, [schedulingType, selectedDate, selectedDatesList, editingDateIndex])

  const timezoneOffsetMinutes = useMemo(() => {
    if (!clientTimezone) {
      return null
    }

    return getTimezoneOffsetMinutes(clientTimezone)
  }, [clientTimezone])

  // Use date conversion utility with timezone
  const convertDateAndSlot = useCallback(
    (selectedDateStr, slotStartUTC, slotEndUTC = null) => {
      return convertDateAndSlotToUTC(
        selectedDateStr,
        slotStartUTC,
        slotEndUTC,
        clientTimezone,
      )
    },
    [clientTimezone],
  )

  const loadMonthSlots = useCallback(
    async (targetMonth, preferredDate) => {
      if (!coachId || !targetMonth) {
        return
      }

      try {
        setIsLoadingSlots(true)
        const normalizedMonth = moment(targetMonth).startOf('month')

        // Ensure we have valid numbers
        if (!normalizedMonth.isValid()) {
          setIsLoadingSlots(false)
          return
        }

        const monthNumber = normalizedMonth.month() + 1 // 0-11 -> 1-12
        const yearNumber = normalizedMonth.year()

        const payload = {
          month: Number(monthNumber),
          year: Number(yearNumber),
        }
        if (schedulingType === 'reschedule') {
          payload.eventId = eventId
          payload.isReschedule = true

          // Add sessionIndex if isConditionalSchedulingEnabled is true and schedulingCondition exists
          if (
            scheduledSession?.isConditionalSchedulingEnabled === true &&
            scheduledSession?.schedulingCondition?.sessionIndex
          ) {
            payload.sessionIndex =
              scheduledSession.schedulingCondition.sessionIndex
          }
        }
        // If editing an unavailable slot, use duration as sowSessionDuration
        // Otherwise use the normal sowSessionDuration
        const durationToUse = editingduration || sowSessionDuration
        if (durationToUse) {
          payload.sowSessionDuration = durationToUse
        }
        if (sowId) {
          payload.sowId = sowId
        }
        if (clientTimezone) {
          payload.clientTimezone = clientTimezone
        }

        const { data } = await API.session.getAvailableSlots(coachId, payload)
        const daysMap = (data?.days || []).reduce((acc, day) => {
          if (day?.date) {
            acc[day.date] = day
          }
          return acc
        }, {})

        setAvailableDays(daysMap)

        const nextSelectableDate = (() => {
          // Check if preferredDate is in the current month being loaded
          const isPreferredDateInCurrentMonth = preferredDate
            ? moment(preferredDate).isSame(normalizedMonth, 'month')
            : false

          // When rescheduling, prefer the original booking date first
          // BUT only if it's in the current month and has slots
          // This ensures the calendar shows the date being rescheduled when viewing that month,
          // but allows auto-selection of first available date when navigating to other months
          if (
            schedulingType === 'reschedule' &&
            preferredDate &&
            isPreferredDateInCurrentMonth &&
            daysMap[preferredDate]?.slots?.length
          ) {
            return preferredDate
          }

          // When editing a specific date in schedule-all flow, always keep the
          // preferred date selected, even if there are no slots for that day.
          // This lets us show an empty slot list for that date (e.g., Dec 24)
          // instead of jumping to another day like Dec 1.
          if (
            schedulingType === 'schedule-all' &&
            editingDateIndexRef.current !== null &&
            preferredDate
          ) {
            return preferredDate
          }

          if (preferredDate && daysMap[preferredDate]?.slots?.length) {
            return preferredDate
          }

          const firstDayWithSlots = Object.values(daysMap).find(
            (day) => day?.slots?.length,
          )

          return firstDayWithSlots?.date || null
        })()

        setSelectedDate(nextSelectableDate)
        setSelectedSlot(null)
      } catch (error) {
        Error.showError(error)
        setAvailableDays({})
        setSelectedDate(null)
        setSelectedSlot(null)
      } finally {
        setIsLoadingSlots(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      coachId,
      sowSessionDuration,
      schedulingType,
      editingDateIndex,
      editingduration,
      clientTimezone,
    ],
  )

  useEffect(() => {
    preferredDateRef.current = selectedDate
  }, [selectedDate])

  useEffect(() => {
    if (!coachId || !currentMonth) {
      return
    }

    // Skip reload if we just finished editing and are checking availability
    if (skipNextLoadRef.current && editingDateIndex === null) {
      skipNextLoadRef.current = false
      return
    }

    loadMonthSlots(currentMonth, preferredDateRef.current)
  }, [
    coachId,
    currentMonth,
    loadMonthSlots,
    schedulingType,
    showSelectedDates,
    editingDateIndex,
    editingduration,
    clientTimezone,
  ])

  useEffect(() => {
    // Check if selectedDate is in the current month being displayed
    if (selectedDate && currentMonth) {
      const selectedDateMoment = moment(selectedDate)
      const isInCurrentMonth =
        selectedDateMoment.month() === currentMonth.month() &&
        selectedDateMoment.year() === currentMonth.year()

      // If selectedDate is not in the current month, clear time slots
      if (!isInCurrentMonth) {
        setTimeSlots([])
        setSelectedSlot(null)
        return
      }
    }

    if (selectedDate && availableDays[selectedDate]?.slots?.length) {
      setTimeSlots(availableDays[selectedDate].slots)
      return
    }

    setTimeSlots([])
  }, [selectedDate, availableDays, currentMonth])

  const handleDateSelect = (date) => {
    // If editing a specific date in schedule-all flow:
    // - If the original day still has some slots, user can only change the time
    //   on that same date (no date change allowed).
    // - If the coach is unavailable the whole day (no slots), allow changing
    //   the date for this particular slot.
    if (schedulingType === 'schedule-all' && editingDateIndex !== null) {
      const editingDateItem = selectedDatesList[editingDateIndex]
      // Always use the original date when this edit started to decide
      // whether the whole day was unavailable or not.
      const baseDate = editingBaseDate || editingDateItem?.date

      if (!editingDateItem || !baseDate) {
        return
      }

      const isEditingSessionRestriction =
        isSessionRestrictionSlot(editingDateItem)

      if (date) {
        const updatedDates = [...selectedDatesList]

        updatedDates[editingDateIndex] = {
          ...editingDateItem,
          date,
          slotStart: null,
          slotEnd: null,
          isSlotAvailable: true,
          isIncluded: true,
          ...(isEditingSessionRestriction
            ? {
                sessionConstraint: null,
                isSameDayConflict: false,
                isWeeklyLimitWarning: false,
                isMaxSessionsPerWeekExceeded: false,
                slotRestrictionMessage: null,
              }
            : {}),
        }

        setSelectedDatesList(updatedDates)
        setSelectedDate(date)
        setSelectedSlot(null)

        const dateMoment = moment(date)
        if (!dateMoment.isValid()) {
          return
        }
        const monthStart = dateMoment.clone().startOf('month')
        preferredDateRef.current = date

        setCurrentMonth((prev) => {
          if (prev && prev.isSame(monthStart, 'month')) {
            return prev
          }
          return monthStart
        })
      }

      return
    }

    // For schedule-all flow, if we already have selected dates list and not editing, clear it when selecting a new date
    if (
      schedulingType === 'schedule-all' &&
      showSelectedDates &&
      editingDateIndex === null &&
      date
    ) {
      handleResetDates()
    }
    setSelectedDate(date)
    setSelectedSlot(null)
  }

  const handleMonthChange = useCallback((month) => {
    if (!month) {
      return
    }

    const normalizedMonth = moment(month).startOf('month')
    setCurrentMonth((prev) => {
      if (prev && prev.isSame(normalizedMonth, 'month')) {
        return prev
      }

      return normalizedMonth
    })
  }, [])

  const getTotalSessions = () => {
    if (schedulingType === 'first-session') {
      return 1
    }
    if (schedulingType === 'remaining' && coachDetails?.remainingSessions) {
      return coachDetails.remainingSessions
    }
    if (schedulingType === 'schedule-all' && coachDetails?.remainingSessions) {
      return coachDetails.remainingSessions
    }
    return 1
  }

  const checkRescheduleSlotAvailability = useCallback(
    async (date, slot) => {
      if (!coachId || !date || !slot) {
        return { type: SlotAvailabilityCheckResult.ALLOWED }
      }

      const durationMinutes = Number(scheduledSession?.duration) || 0
      const { start: slotStart } = convertDateAndSlotToUTC(
        date,
        slot.start,
        slot.end,
        clientTimezone,
      )

      const payload = {
        selectedSlots: [slotStart.toISOString()],
        sowSessionDuration: durationMinutes,
        ...(sowId ? { sowId } : {}),
        ...(eventId ? { eventId } : {}),
      }

      const { data } = await API.session.checkSlotAvailability(coachId, payload)

      if (!data?.success || !Array.isArray(data.data) || !data.data[0]) {
        return { type: SlotAvailabilityCheckResult.ALLOWED }
      }

      return evaluateSlotAvailabilityItem(data.data[0])
    },
    [coachId, clientTimezone, scheduledSession?.duration, sowId, eventId],
  )

  const handleSlotSelect = async (slot) => {
    if (schedulingType === 'reschedule' && slot && selectedDate) {
      setIsCheckingAvailability(true)

      try {
        const result = await checkRescheduleSlotAvailability(selectedDate, slot)

        if (result.type === SlotAvailabilityCheckResult.HARD_BLOCK) {
          Notification.showNotification(
            result.message ||
              'Unable to reschedule to this time. Please choose another slot.',
            'danger',
          )
          setSelectedSlot(null)
          return
        }

        if (result.type === SlotAvailabilityCheckResult.UNAVAILABLE) {
          Notification.showNotification(
            result.message ||
              'Coach is not available at this time. Please choose another slot.',
            'danger',
          )
          setSelectedSlot(null)
          return
        }

        setSelectedSlot(slot)

        if (result.type === SlotAvailabilityCheckResult.WEEKLY_WARNING) {
          setRescheduleSlotWeeklyModal({
            isOpen: true,
            message: result.message,
          })
        }
      } catch (error) {
        Error.showError(error)
        setSelectedSlot(null)
      } finally {
        setIsCheckingAvailability(false)
      }
      return
    }

    setSelectedSlot(slot)

    if (
      schedulingType === 'schedule-all' &&
      editingDateIndex !== null &&
      slot
    ) {
      const updatedDates = [...selectedDatesList]
      const editingDate = updatedDates[editingDateIndex]
      const originalDate = editingDate?.date

      if (!originalDate) {
        return
      }

      const isEditingSessionRestriction = isSessionRestrictionSlot(editingDate)
      const dateForSlot =
        isEditingSessionRestriction && selectedDate
          ? selectedDate
          : originalDate

      const durationMinutes = Number(sowSessionDuration) || 0

      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlotToUTC(
          dateForSlot,
          slot.start,
          slot.end,
          clientTimezone,
        )

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      updatedDates[editingDateIndex] = {
        ...editingDate,
        date: dateForSlot,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
        isSlotAvailable: true,
        isIncluded: true,
        sessionConstraint: null,
        isSameDayConflict: false,
        isWeeklyLimitWarning: false,
        isMaxSessionsPerWeekExceeded: false,
        slotRestrictionMessage: null,
      }

      setSelectedDatesList(updatedDates)
      editingDateIndexRef.current = null
      setEditingDateIndex(null)
      setEditingduration(null)
      skipNextLoadRef.current = true

      await checkSlotAvailabilityForDates(updatedDates)
      return
    }

    // For schedule-all flow, check if only one session remaining
    if (schedulingType === 'schedule-all' && slot && selectedDate) {
      const totalSessions = getTotalSessions()

      // If only one session, skip recurring pattern modal and directly add to list
      if (totalSessions === 1) {
        const durationMinutes = Number(sowSessionDuration) || 0

        // Convert selected date + slot time to UTC (accounting for user's timezone)
        const { start: slotStart, end: slotEndFromConversion } =
          convertDateAndSlot(selectedDate, slot.start, slot.end)

        const slotEnd = durationMinutes
          ? slotStart.clone().add(durationMinutes, 'minutes')
          : slotEndFromConversion || slotStart.clone()

        const singleDate = {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
          isSlotAvailable: true, // Will be checked
        }

        setSelectedDatesList([singleDate])
        setShowSelectedDates(true)

        // Check slot availability for the single date
        checkSlotAvailabilityForDates([singleDate])
      } else {
        // For multiple sessions, show recurring pattern modal
        setShowScheduleSettingsModal(true)
      }
    }
  }

  // handleTimezoneChange is provided by useSessionTimezone hook

  // Reset scheduling flow when timezone changes (if dates are already selected)
  const prevTimezoneRef = useRef(clientTimezone)
  useEffect(() => {
    // Skip on initial mount
    if (prevTimezoneRef.current === clientTimezone) {
      return
    }

    // If timezone changed and we have selected dates in schedule-all flow, reset everything
    if (
      schedulingType === 'schedule-all' &&
      (selectedDatesList.length > 0 || showSelectedDates)
    ) {
      // Reset all schedule-all flow state
      setSelectedDatesList([])
      setShowSelectedDates(false)
      setSelectedSlot(null)
      setEditingDateIndex(null)
      setEditingBaseDate(null)
      // Reset calendar to current month and today's date
      const today = moment().format('YYYY-MM-DD')
      setSelectedDate(today)
      setCurrentMonth(moment().startOf('month'))
    } else if (selectedSlot) {
      // For other scheduling types (first-session, remaining), reset selected slot
      // This ensures time slots are reloaded with new timezone
      setSelectedSlot(null)
    }

    // Update ref for next comparison
    prevTimezoneRef.current = clientTimezone
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientTimezone, schedulingType])

  // Generate recurring dates using utility function
  const generateRecurringDatesForPattern = useCallback(
    (startDate, pattern, totalSessions, timeSlot) => {
      return generateRecurringDates(
        startDate,
        pattern,
        totalSessions,
        timeSlot,
        convertDateAndSlot,
        sowSessionDuration,
      )
    },
    [convertDateAndSlot, sowSessionDuration],
  )

  // Handle recurring pattern selection
  const handleRecurringPatternConfirm = async (pattern) => {
    if (!selectedDate || !selectedSlot || !pattern) {
      return
    }

    const totalSessions = getTotalSessions()

    // Generate recurring dates
    const generatedDates = generateRecurringDatesForPattern(
      selectedDate,
      pattern,
      totalSessions,
      selectedSlot,
    )

    setSelectedDatesList(generatedDates)
    setShowSelectedDates(true)

    // Check slot availability for all dates
    await checkSlotAvailabilityForDates(generatedDates)
  }

  // Check slot availability for all dates
  const checkSlotAvailabilityForDates = async (dates) => {
    if (!coachId || !dates || dates.length === 0) {
      return
    }

    try {
      setIsCheckingAvailability(true)

      const selectedSlots = dates.map((dateItem) => dateItem.slotStart)

      const payload = {
        selectedSlots,
        sowSessionDuration: sowSessionDuration || 0,
        sowId,
      }

      const { data } = await API.session.checkSlotAvailability(coachId, payload)

      if (data?.success && Array.isArray(data.data)) {
        setSelectedDatesList(mergeDatesWithAvailability(dates, data.data))
      }
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsCheckingAvailability(false)
    }
  }

  // Handle click on date in selected dates list to edit
  const handleDateClickFromList = (dateItem, index) => {
    if (dateItem.isSlotAvailable === false) {
      editingDateIndexRef.current = index
      preferredDateRef.current = dateItem.date
      setEditingDateIndex(index)
      setEditingBaseDate(dateItem.date || null)
      setEditingduration(dateItem.duration || null)
      skipNextLoadRef.current = false
      setSelectedDate(dateItem.date)
      setSelectedSlot(null)
      const dateMoment = moment(dateItem.date)
      if (!dateMoment.isValid()) {
        return
      }
      const monthStart = dateMoment.clone().startOf('month')
      setCurrentMonth(monthStart)
    }
  }

  const handleToggleInclude = (index) => {
    // Don't change ticks while availability is being checked
    if (isCheckingAvailability) {
      return
    }
    setSelectedDatesList((prev) => toggleDateIncludedInList(prev, index))
  }

  const handleBackToAllDates = () => {
    editingDateIndexRef.current = null
    setEditingDateIndex(null)
    setEditingBaseDate(null)
    setEditingduration(null)
    skipNextLoadRef.current = false // Reset flag when going back
    setSelectedDate(selectedDatesList[0]?.date || moment().format('YYYY-MM-DD'))
  }

  // Reset selected dates
  const handleResetDates = () => {
    editingDateIndexRef.current = null
    setSelectedDatesList([])
    setShowSelectedDates(false)
    setSelectedSlot(null)
    setEditingDateIndex(null)
    setEditingBaseDate(null)
    setEditingduration(null)
    skipNextLoadRef.current = false // Reset flag when resetting
    // Reset calendar to current month and today's date
    const today = moment().format('YYYY-MM-DD')
    setSelectedDate(today)
    setCurrentMonth(moment().startOf('month'))
  }

  const getPageTitle = () => {
    switch (schedulingType) {
      case 'reschedule':
        return 'Reschedule Session'
      case 'remaining':
        return 'Schedule More Sessions'
      case 'schedule-all':
        // If only one session remaining, show singular title
        const totalSessions = getTotalSessions()
        return totalSessions === 1
          ? 'Schedule More Session'
          : 'Schedule Multiple Sessions'
      default:
        return 'Schedule Session'
    }
  }

  const handleSchedule = async () => {
    // For schedule-all flow, check selectedDatesList instead
    if (schedulingType === 'schedule-all') {
      if (!selectedDatesList || selectedDatesList.length === 0) {
        Error.showError({ message: 'Please select dates for scheduling' })
        return
      }
      // First session is compulsory while nothing is booked yet
      if (
        isFirstSessionScheduling(coachDetails) &&
        isFirstSessionMissing(selectedDatesList)
      ) {
        Notification.showNotification(FIRST_SESSION_REQUIRED_MESSAGE, 'danger')
        return
      }
    } else {
      if (!selectedDate || !selectedSlot) {
        Error.showError({ message: 'Please select a date and time slot' })
        return
      }
    }

    if (!sowId && schedulingType !== 'reschedule') {
      Notification.showNotification(
        'Unable to find coaching plan details. Please try again later.',
        'danger',
      )
      return
    }

    try {
      setIsScheduling(true)

      if (schedulingType === 'reschedule') {
        const sessionDateTime = selectedDate
          ? `${moment(selectedDate).format('MM/DD/YYYY')} ${
              clientTimezone
                ? moment
                    .utc(selectedSlot.start)
                    .tz(clientTimezone)
                    .format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm')
                : moment
                    .utc(selectedSlot.start)
                    .format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm')
            }`
          : '-'

        gtmRescheduleSessionAction(
          getSessionLabelForTracking(scheduledSession),
          sessionDateTime,
          user?._id,
          scheduledSession?.questId,
        )

        const result = await handleRescheduleSession({
          eventId,
          coachId,
          selectedDate,
          selectedSlot,
          scheduledSession,
          clientTimezone,
          API,
          Notification,
          history,
          onScheduleSuccess,
        })

        if (result?.weeklyWarning) {
          pendingRescheduleSubmitRef.current = true
          setRescheduleSlotWeeklyModal({
            isOpen: true,
            message: result.message,
          })
        }
      } else if (schedulingType === 'first-session') {
        await handleScheduleFirstSession({
          selectedDate,
          selectedSlot,
          sowId,
          sowSessionDuration,
          clientTimezone,
          API,
          Notification,
          history,
          onScheduleSuccess,
          runScheduling,
        })
      } else if (schedulingType === 'remaining') {
        await handleScheduleRemainingSessions({
          selectedDate,
          selectedSlot,
          sowId,
          clientTimezone,
          API,
          Notification,
          history,
          onScheduleSuccess,
        })
      } else if (schedulingType === 'schedule-all') {
        const sessionCount =
          getSchedulableDatesFromList(selectedDatesList || []).length ||
          getTotalSessions() ||
          0
        const coach = getFname(coachDetails?.coach)
        const client = getFname(coachDetails?.client)
        const sessionLabel =
          coach !== '-' || client !== '-'
            ? `Coaching Plan: ${coach} & ${client}`
            : 'Coaching Plan'

        gtmScheduleAllSessionAction(
          sessionLabel,
          user?._id,
          coachDetails?.questId,
          sessionCount,
        )

        await handleScheduleAllSessions({
          selectedDatesList,
          sowId,
          sowSessionDuration,
          clientTimezone,
          API,
          Notification,
          history,
          onScheduleSuccess,
          runScheduling,
          isConditionalSchedulingEnabled:
            coachDetails?.isConditionalSchedulingEnabled === true,
        })
      }
    } catch (error) {
      if (!isWeeklyWarning(error) && !isHardSchedulingBlock(error)) {
        Error.showError(error)
      }
    } finally {
      setIsScheduling(false)
    }
  }

  const isSubmitting = isScheduling || isConfirmingWeekly

  const getButtonText = () => {
    if (isSubmitting) {
      return 'SCHEDULING...'
    }
    if (
      isCheckingAvailability &&
      (schedulingType === 'schedule-all' || schedulingType === 'reschedule')
    ) {
      return 'CHECKING AVAILABILITY...'
    }
    switch (schedulingType) {
      case 'reschedule':
        return 'RESCHEDULE'
      case 'remaining':
        return 'SCHEDULE'
      case 'schedule-all':
        // If only one session remaining, show "SCHEDULE" instead of "Schedule Multiple Sessions"
        const totalSessions = getTotalSessions()
        if (totalSessions !== 1 && showSelectedDates && schedulableCount > 0) {
          return `SCHEDULE ${schedulableCount} ${
            schedulableCount === 1 ? 'SESSION' : 'SESSIONS'
          }`
        }
        return totalSessions === 1 ? 'SCHEDULE' : 'Schedule Multiple Sessions'
      default:
        return 'SCHEDULE'
    }
  }

  // Check if there are any unavailable slots in schedule-all flow
  const hasBlockingSlots = useMemo(() => {
    if (schedulingType !== 'schedule-all' || !showSelectedDates) {
      return false
    }
    return hasBlockingUnavailableSlots(selectedDatesList)
  }, [schedulingType, showSelectedDates, selectedDatesList])

  const schedulableCount = useMemo(
    () => getSchedulableDatesFromList(selectedDatesList || []).length,
    [selectedDatesList],
  )

  const unavailableSlotsTooltip = useMemo(
    () => getUnavailableSlotsTooltip(selectedDatesList),
    [selectedDatesList],
  )

  // First session is compulsory while nothing is booked yet
  const isFirstSessionBlocked = useMemo(() => {
    if (schedulingType !== 'schedule-all' || !showSelectedDates) {
      return false
    }
    return (
      isFirstSessionScheduling(coachDetails) &&
      isFirstSessionMissing(selectedDatesList)
    )
  }, [schedulingType, showSelectedDates, coachDetails, selectedDatesList])

  // Check if button should be disabled for schedule-all flow
  const isButtonDisabled = useMemo(() => {
    if (isSubmitting) {
      return true
    }
    // Disable button when checking availability
    if (isCheckingAvailability) {
      return true
    }
    if (schedulingType === 'reschedule' && (!selectedDate || !selectedSlot)) {
      return true
    }
    if (schedulingType === 'schedule-all') {
      if (isFirstSessionBlocked) {
        return true
      }
      // Disable if no dates selected or if there are unavailable slots
      // Enabled as long as at least one date is error-free; the submit
      // handlers send only those dates.
      return !selectedDatesList || schedulableCount === 0
    }
    return false
  }, [
    isSubmitting,
    isCheckingAvailability,
    schedulingType,
    selectedDatesList,
    schedulableCount,
    isFirstSessionBlocked,
  ])

  return (
    <div className='first-session-schedule'>
      <div className='s1'>{getPageTitle()}</div>
      <Container className='first-session-schedule'>
        <CoachDetailsSection
          coachName={coachName}
          duration={
            hasGrowthInRemaining ? firstSessionDuration : allSessionsDuration
          }
          totalSessions={getTotalSessions()}
          clientTimezone={clientTimezone}
          onTimezoneChange={handleTimezoneChange}
          firstSlotDuration={firstSlotDuration}
          otherSlotsDuration={otherSlotsDuration}
          hasGrowthInRemaining={hasGrowthInRemaining}
          schedulingType={schedulingType}
          sowType={sowType}
        />

        <Row className='mt-32px first-session-schedule__planner border border-dark border-1 rounded'>
          <Col
            lg={6}
            md={12}
            className='pl-20px pr-20px border border-dark border-1 border-top-0 border-bottom-0 border-start-0 first-session-schedule__planner-column'>
            <SessionDatePicker
              selectedDate={
                schedulingType === 'schedule-all' && showSelectedDates
                  ? editingDateIndex !== null
                    ? selectedDate
                    : selectedDatesList.map((item) => item.date)
                  : selectedDate
              }
              highlightedDates={
                schedulingType === 'schedule-all' && showSelectedDates
                  ? selectedDatesList.map((item) => item.date)
                  : []
              }
              onDateSelect={handleDateSelect}
              allowMultipleSelection={
                schedulingType === 'schedule-all' &&
                showSelectedDates &&
                editingDateIndex === null
                  ? true
                  : schedulingType === 'schedule-all'
                  ? false
                  : allowMultipleSelection
              }
              minDate={moment().format('YYYY-MM-DD')}
              currentMonth={currentMonth}
              onMonthChange={handleMonthChange}
              availableDays={availableDays}
              unavailableDates={
                schedulingType === 'schedule-all' && showSelectedDates
                  ? selectedDatesList
                      .filter(
                        (item) =>
                          item.isSlotAvailable === false &&
                          !isSessionRestrictionSlot(item),
                      )
                      .map((item) => item.date)
                  : []
              }
              restrictionDates={
                schedulingType === 'schedule-all' && showSelectedDates
                  ? selectedDatesList
                      .filter(
                        (item) =>
                          item.isSlotAvailable === false &&
                          isSessionRestrictionSlot(item),
                      )
                      .map((item) => item.date)
                  : []
              }
            />
          </Col>
          <Col
            lg={6}
            md={12}
            className='first-session-schedule__planner-column'>
            {schedulingType === 'schedule-all' &&
            showSelectedDates &&
            editingDateIndex === null ? (
              <SelectedDatesList
                selectedDates={selectedDatesList}
                timeFormat={timeFormat}
                timezoneOffsetMinutes={timezoneOffsetMinutes}
                timezone={clientTimezone}
                onDateClick={handleDateClickFromList}
                onReset={handleResetDates}
                onToggleInclude={handleToggleInclude}
                isCheckingAvailability={isCheckingAvailability}
                showBackButton={false}
                onBackClick={handleBackToAllDates}
              />
            ) : (
              <TimeSlotPicker
                slots={timeSlots}
                selectedSlot={selectedSlot}
                onSlotSelect={handleSlotSelect}
                timeFormat={timeFormat}
                onTimeFormatChange={setTimeFormat}
                isLoading={isLoadingSlots}
                timezoneOffsetMinutes={timezoneOffsetMinutes}
                timezone={clientTimezone}
                takenSlots={takenSlotsForCurrentDate}
                showBackButton={
                  schedulingType === 'schedule-all' && editingDateIndex !== null
                }
                onBackClick={handleBackToAllDates}
              />
            )}
          </Col>
          <Col
            lg={12}
            md={12}
            className='d-flex px-0 justify-content-end mt-32px'>
            <ScheduleButton
              buttonText={getButtonText()}
              isDisabled={isButtonDisabled}
              hasUnavailableSlots={hasBlockingSlots || isFirstSessionBlocked}
              unavailableTooltip={
                isFirstSessionBlocked
                  ? FIRST_SESSION_REQUIRED_MESSAGE
                  : unavailableSlotsTooltip ||
                    'Coach is not available, please change it first'
              }
              onClick={handleSchedule}
              isLoading={isSubmitting || isCheckingAvailability}
            />
          </Col>
        </Row>
      </Container>
      <ScheduleSettingsModal
        show={showScheduleSettingsModal}
        handleClose={() => setShowScheduleSettingsModal(false)}
        onConfirm={handleRecurringPatternConfirm}
        selectedDate={selectedDate}
        coachDetails={coachDetails}
      />
      <WeeklySessionWarningModal
        isOpen={weeklyModal.isOpen}
        message={weeklyModal.message}
        onConfirm={handleWeeklyConfirm}
        onCancel={closeWeeklyModal}
        loading={isConfirmingWeekly}
      />
      <WeeklySessionWarningModal
        isOpen={rescheduleSlotWeeklyModal.isOpen}
        message={rescheduleSlotWeeklyModal.message}
        onConfirm={async () => {
          setRescheduleSlotWeeklyModal({ isOpen: false, message: '' })
          if (pendingRescheduleSubmitRef.current) {
            pendingRescheduleSubmitRef.current = false
            await handleSchedule()
          }
        }}
        onCancel={() => {
          const wasSubmitPending = pendingRescheduleSubmitRef.current
          pendingRescheduleSubmitRef.current = false
          setRescheduleSlotWeeklyModal({ isOpen: false, message: '' })
          if (!wasSubmitPending) {
            setSelectedSlot(null)
          }
        }}
        loading={isScheduling || isCheckingAvailability}
      />
    </div>
  )
}

export default SessionSchedulingCalendar
